import { AvailableSlots } from '@shared/components/date-picker/model/available-slots';
import { Link } from '@shared/models';

/** Raw appointment-slots payload, before the front-end normalization that derives `cta`. */
export type RawAvailableSlots = Omit<AvailableSlots, 'cta'>;

/** Matches a leading `[META]{...}[/META]` block and captures its JSON content. */
const META_TAG = /^\s*\[META\]([\s\S]*?)\[\/META\]\s*/;

interface ExtractedCta {
  cta: Link | null;
  cleaned: string;
}

/**
 * Parses the `[META]` JSON payload into a CTA. Tolerates a backend that double-escapes the
 * inner quotes (`{\"label\":\"x\"}` instead of clean JSON) by retrying once with the stray
 * backslashes stripped. Returns null when the payload cannot be parsed or lacks non-empty
 * `label`/`url` strings.
 */
function parseCtaPayload(raw: string): Link | null {
  for (const candidate of [raw, raw.replace(/\\+"/g, '"')]) {
    try {
      const payload: unknown = JSON.parse(candidate);
      if (
        typeof payload === 'object' &&
        payload !== null &&
        'label' in payload &&
        'url' in payload &&
        typeof payload.label === 'string' &&
        payload.label.trim().length > 0 &&
        typeof payload.url === 'string' &&
        payload.url.trim().length > 0
      ) {
        return { label: payload.label, url: payload.url };
      }
    } catch {
      // Not parseable as-is: try the de-escaped candidate, then give up.
    }
  }
  return null;
}

/**
 * Extracts a CTA encoded as a leading `[META]{"label":"...","url":"..."}[/META]` JSON block.
 * The tag is always stripped from the returned text, even when its payload is invalid,
 * so no residue is ever rendered.
 */
function extractMetaCta(text: string): ExtractedCta {
  const match = META_TAG.exec(text);
  if (!match) {
    return { cta: null, cleaned: text };
  }

  return { cta: parseCtaPayload(match[1] ?? ''), cleaned: text.replace(META_TAG, '') };
}

/**
 * Normalizes the raw appointment-slots payload: extracts an optional CTA from the message
 * and strips its `[META]` tag from both `bodyText` and `bodyHtml`. The API contract is
 * untouched; `cta` is a front-end-only field. The CTA is parsed from `bodyText` (plain text,
 * safer than parsing around HTML).
 */
export function normalizeAvailableSlots(raw: RawAvailableSlots): AvailableSlots {
  if (!raw.message) {
    return { ...raw, cta: null };
  }

  const { cta, cleaned: bodyText } = extractMetaCta(raw.message.bodyText);
  const { cleaned: bodyHtml } = extractMetaCta(raw.message.bodyHtml);

  return {
    ...raw,
    cta,
    message: { ...raw.message, bodyText, bodyHtml },
  };
}
